import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    IconButton,
    Input,
    Portal,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip, HiXMark } from "react-icons/hi2";

const TAG_MAP = [
    { api: "{{firstname}}",   pl: "{{imie}}",       en: "{{firstname}}",   de: "{{vorname}}"  },
    { api: "{{lastname}}",    pl: "{{nazwisko}}",   en: "{{lastname}}",    de: "{{nachname}}" },
    { api: "{{jobTitle}}",    pl: "{{stanowisko}}", en: "{{jobTitle}}",    de: "{{stelle}}"   },
    { api: "{{companyName}}", pl: "{{firma}}",      en: "{{companyName}}", de: "{{firma}}"    },
];

const normalizeBody = (text) => {
    let result = text;
    for (const entry of TAG_MAP) {
        for (const [key, display] of Object.entries(entry)) {
            if (key !== "api" && display !== entry.api) {
                result = result.replaceAll(display, entry.api);
            }
        }
    }
    return result;
};

const BulkEmailModal = ({ isOpen, onClose, count, onSubmit, isLoading, title }) => {
    const { t, i18n } = useTranslation("models");
    const displayTags = TAG_MAP.map(e => e[i18n.language] ?? e.en);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSubject("");
            setBody("");
            setFiles([]);
        }
    }, [isOpen]);

    const insertTag = (tag) => {
        const el = bodyRef.current;
        if (!el) {
            setBody(prev => prev + tag);
            return;
        }
        const start = el.selectionStart ?? body.length;
        const end = el.selectionEnd ?? body.length;
        const next = body.slice(0, start) + tag + body.slice(end);
        setBody(next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + tag.length, start + tag.length);
        });
    };

    const handleSubmit = () => {
        onSubmit(subject.trim(), normalizeBody(body.trim()), files);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="bg.card"
                        color="white"
                        borderColor="border.primary"
                        border="1px solid"
                        w={{ base: "calc(100vw - 2rem)", md: "2xl" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4}>
                                {title ?? t("candidatePage.bulkEmailModalTitle", { count })}
                            </Text>
                            <VStack align="stretch" gap={4}>
                                <Box>
                                    <Text fontSize="sm" color="text.grey" mb={2}>
                                        {t("candidatePage.bulkEmailSubject")}
                                    </Text>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        bg="bg.secondary"
                                        border="1px solid"
                                        borderColor="border.primary"
                                        color="white"
                                        _placeholder={{ color: "text.grey" }}
                                    />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color="text.grey" mb={2}>
                                        {t("candidatePage.bulkEmailBody")}
                                    </Text>
                                    <Textarea
                                        ref={bodyRef}
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        rows={8}
                                        bg="bg.secondary"
                                        border="1px solid"
                                        borderColor="border.primary"
                                        color="white"
                                        _placeholder={{ color: "text.grey" }}
                                        resize="vertical"
                                    />
                                    <HStack mt={2} gap={2} flexWrap="wrap">
                                        <Text fontSize="xs" color="text.grey">
                                            {t("candidatePage.bulkEmailTags")}
                                        </Text>
                                        {displayTags.map(tag => (
                                            <Box
                                                key={tag}
                                                as="button"
                                                onClick={() => insertTag(tag)}
                                                px={2}
                                                py={0.5}
                                                borderRadius={4}
                                                border="1px solid"
                                                borderColor="border.primary"
                                                bg="bg.secondary"
                                                color="brand.400"
                                                fontSize="xs"
                                                cursor="pointer"
                                                _hover={{ borderColor: "brand.400" }}
                                                transition="all 0.15s"
                                            >
                                                {tag}
                                            </Box>
                                        ))}
                                    </HStack>
                                </Box>
                            </VStack>
                            <Box mt={4}>
                                <Text fontSize="sm" color="text.grey" mb={2}>
                                    {t("candidateDetailPage.noteAttachmentsLabel")}
                                </Text>
                                <Button
                                    as="label"
                                    htmlFor="email-file-input"
                                    variant="outlined"
                                    size="sm"
                                    cursor="pointer"
                                    mb={files.length > 0 ? 3 : 0}
                                >
                                    <HiPaperClip />
                                    {t("candidateDetailPage.noteAttachSelectFiles")}
                                    <input
                                        id="email-file-input"
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files ?? []);
                                            setFiles(prev => [...prev, ...picked]);
                                            e.target.value = "";
                                        }}
                                    />
                                </Button>
                                {files.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        {files.map((f, i) => (
                                            <HStack
                                                key={i}
                                                bg="bg.secondary"
                                                borderRadius={8}
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack gap={2} minW={0}>
                                                    <Box color="text.grey" flexShrink={0}><HiPaperClip /></Box>
                                                    <Text fontSize="xs" color="white" fontFamily="mono" truncate>{f.name}</Text>
                                                </HStack>
                                                <IconButton
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={t("candidateDetailPage.noteRemoveFile")}
                                                    color="text.grey"
                                                    flexShrink={0}
                                                    onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                                                >
                                                    <HiXMark />
                                                </IconButton>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                            <HStack justify="flex-end" gap={2} mt={6}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.changeStatusModalCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!subject.trim() || !body.trim()}
                                    onClick={handleSubmit}
                                >
                                    {t("candidatePage.bulkEmailSend")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="text.grey" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default BulkEmailModal;
